import { Link } from 'react-router-dom';
import { 
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ShieldCheckIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import Logo from './Logo';

const ComplianceLogo = ({ name }) => {
  const logos = {
    SEC: (
      <svg className="h-12 w-auto" viewBox="0 0 140 60" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="10" y="10" width="120" height="40" rx="4" className="fill-current opacity-10"/>
        <path 
          d="M35 20h70v20H35V20zm2 2h66v16H37V22z M45 30h50 M45 25h50 M45 35h50" 
          className="stroke-current" 
          strokeWidth="1.5"
        />
        <path
          d="M55 15h30c1.1 0 2 .9 2 2v26c0 1.1-.9 2-2 2H55c-1.1 0-2-.9-2-2V17c0-1.1.9-2 2-2z"
          className="fill-current opacity-10 stroke-current"
          strokeWidth="1"
        />
        <path
          className="fill-current"
          d="M60 28h20v4H60z"
        />
        <text x="70" y="45" className="fill-current text-[8px]" textAnchor="middle" fontFamily="Arial">
          SEC REGISTERED
        </text>
      </svg>
    ),
    FINRA: (
      <svg className="h-12 w-auto" viewBox="0 0 140 60" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="10" y="10" width="120" height="40" rx="4" className="fill-current opacity-10"/>
        <path 
          d="M35 25h70M35 35h70" 
          className="stroke-current" 
          strokeWidth="2" 
          strokeLinecap="round"
        />
        <path
          className="fill-current"
          d="M50 20h40v6H50zM50 34h40v6H50z"
        />
        <text x="70" y="45" className="fill-current text-[8px]" textAnchor="middle" fontFamily="Arial">
          FINRA MEMBER
        </text>
      </svg>
    ),
    SIPC: (
      <svg className="h-12 w-auto" viewBox="0 0 140 60" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="10" y="10" width="120" height="40" rx="4" className="fill-current opacity-10"/>
        <path
          d="M60 20c-11 0-20 9-20 20h40c0-11-9-20-20-20zm0 10c-5.5 0-10 4.5-10 10h20c0-5.5-4.5-10-10-10z"
          className="fill-current opacity-20"
        />
        <path
          d="M60 25c-8.3 0-15 6.7-15 15h30c0-8.3-6.7-15-15-15z"
          className="fill-current"
        />
        <text x="70" y="45" className="fill-current text-[8px]" textAnchor="middle" fontFamily="Arial">
          SIPC PROTECTED
        </text>
      </svg>
    ),
    SSL: (
      <svg className="h-12 w-auto" viewBox="0 0 140 60" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="10" y="10" width="120" height="40" rx="4" className="fill-current opacity-10"/>
        <path
          d="M60 15c-8.3 0-15 6.7-15 15v5h-5v15h40v-15h-5v-5c0-8.3-6.7-15-15-15zm0 5c5.5 0 10 4.5 10 10v5H50v-5c0-5.5 4.5-10 10-10z"
          className="fill-current"
        />
        <text x="70" y="45" className="fill-current text-[8px]" textAnchor="middle" fontFamily="Arial">
          SSL SECURED
        </text>
      </svg>
    )
  };

  return (
    <div className="text-gray-400 hover:text-gray-300 transition-colors duration-300">
      {logos[name]}
    </div>
  );
};

function Footer() {
  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <div className="invert">
              <Logo className="h-8 w-auto mb-4" />
            </div>
            <p className="text-sm text-gray-400 mb-4">
              Quickwealth Investment Group LLC is a registered investment advisor providing comprehensive investment solutions.
            </p>
            <div className="space-y-2">
              <a href="tel:+1 (424) 292-0631" className="flex items-center text-sm hover:text-white">
                <PhoneIcon className="h-4 w-4 mr-2" />
                +1 (424) 292-0631
              </a>
              <a href="mailto:support@quickwealthinvestment.com" className="flex items-center text-sm hover:text-white">
                <EnvelopeIcon className="h-4 w-4 mr-2" />
                support@quickwealthinvestment.com
              </a>
              <div className="flex items-center text-sm">
                <MapPinIcon className="h-4 w-4 mr-2" />
                6930 Beach Blvd | 141, Buena Park. CA 90621
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li><Link to="/about" className="text-sm hover:text-white">About Us</Link></li>
              <li><Link to="/portfolio" className="text-sm hover:text-white">Our Portfolio</Link></li>
              <li><Link to="/contact" className="text-sm hover:text-white">Contact</Link></li>
              <li><Link to="/careers" className="text-sm hover:text-white">Careers</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Legal</h3>
            <ul className="space-y-2">
              <li><Link to="/privacy" className="text-sm hover:text-white">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-sm hover:text-white">Terms of Service</Link></li>
              <li><Link to="/disclosures" className="text-sm hover:text-white">Disclosures</Link></li>
              <li><Link to="/regulatory" className="text-sm hover:text-white">Regulatory Information</Link></li>
            </ul>
          </div>

          {/* Compliance */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Compliance</h3>
            <p className="text-sm text-gray-400 mb-4">
              Member FINRA / SIPC. Registered SEC Investment Advisor.
            </p>
            <div className="grid grid-cols-2 gap-6">
              <ComplianceLogo name="SEC" />
              <ComplianceLogo name="FINRA" />
              <ComplianceLogo name="SIPC" />
              <ComplianceLogo name="SSL" />
            </div>
          </div>
        </div>

        {/* Compliance Text */}
        <div className="border-t border-gray-800 pt-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="text-xs text-gray-400">
              <p className="mb-2">
                Important Disclosures: Investment advisory services offered through Quickwealth Investment Group LLC LLC, 
                an SEC-registered investment advisor. Registration does not imply a certain level 
                of skill or training.
              </p>
              <p>
                Securities offered through Quickwealth Investment Group LLC Securities, Member FINRA/SIPC. 
                Quickwealth Investment Group LLC LLC and Quickwealthstment Group LLC Securities are affiliated companies.
              </p>
            </div>
            <div className="text-xs text-gray-400 md:text-right">
              <p className="mb-2">
                © {new Date().getFullYear()} Quickwealth Investment Group LLC LLC. All rights reserved.
              </p>
              <p>
                Check the background of our firm on FINRA's BrokerCheck.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer; 